import { Avatar, Box, Button, IconButton, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AlertDialogSlide from 'common/components/dialog/generalDialog';
import LoadingProgress from 'common/components/progress/loading';
import { BASE_S3, COMPANY_COLORS } from 'config/consts';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const generalColor = COMPANY_COLORS.general.general_color;
const PODIUM_COLORS = { 1: '#E0B93C', 2: '#B9B7B2', 3: '#D08A63' };
const PODIUM_HEIGHTS = { 1: 88, 2: 64, 3: 48 };

const TABS = [
  {
    key: 'fastest',
    label: 'Terminou primeiro',
    empty: 'Ninguém concluiu todas as atividades desta jornada ainda.',
  },
  {
    key: 'correct',
    label: 'Mais acertos',
    empty: 'Nenhum acerto registrado nas atividades desta jornada ainda.',
  },
  {
    key: 'points',
    label: 'Mais pontos',
    empty: 'Nenhum ponto distribuído nas atividades desta jornada ainda.',
  },
];

function formatDuration(seconds) {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  if (seconds < 3600) {
    const rest = seconds % 60;
    return rest > 0
      ? `${Math.floor(seconds / 60)} min ${rest}s`
      : `${Math.floor(seconds / 60)} min`;
  }
  if (seconds < 86400) {
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0
      ? `${Math.floor(seconds / 3600)}h ${minutes}min`
      : `${Math.floor(seconds / 3600)}h`;
  }
  const hours = Math.floor((seconds % 86400) / 3600);
  return hours > 0
    ? `${Math.floor(seconds / 86400)}d ${hours}h`
    : `${Math.floor(seconds / 86400)}d`;
}

function formatValue(tabKey, value) {
  if (value === null || value === undefined) {
    return '-';
  }
  if (tabKey === 'fastest') {
    return formatDuration(value);
  }
  if (tabKey === 'correct') {
    return value === 1 ? '1 acerto' : `${value} acertos`;
  }
  return `${Number.isInteger(value) ? value : value.toFixed(2).replace('.', ',')} pts`;
}

function shortName(name) {
  const parts = `${name || ''}`.trim().split(' ').filter(Boolean);
  if (parts.length < 2) {
    return parts[0] || '';
  }
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function initials(name) {
  const parts = `${name || ''}`.trim().split(' ').filter(Boolean);
  if (parts.length < 1) {
    return '?';
  }
  const last = parts.length > 1 ? parts[parts.length - 1] : '';
  return `${parts[0][0]}${last ? last[0] : ''}`.toUpperCase();
}

function JourneyRanking({
  visible,
  onClose,
  loading,
  ranking,
  userId,
  journeyName,
  error,
  onRetry,
}) {
  const [tabKey, setTabKey] = useState(TABS[0].key);

  useEffect(() => {
    if (visible) {
      setTabKey(TABS[0].key);
    }
  }, [visible]);

  const tab = TABS.find((item) => item.key === tabKey);
  const data = ranking?.rankings?.[tabKey];
  const isCurrentUser = (item) => parseInt(item.user_id) === parseInt(userId);

  function renderAvatar(item, size, background) {
    return (
      <Avatar
        alt={item.name}
        src={item.image ? `${BASE_S3}/${item.image}` : undefined}
        style={{
          width: size,
          height: size,
          background: item.image ? undefined : background,
          color: '#fff',
          fontSize: size / 2.6,
          fontWeight: 'bold',
        }}
      >
        {isCurrentUser(item) ? 'V' : initials(item.name)}
      </Avatar>
    );
  }

  function renderTabs() {
    return (
      <Box display="flex" style={{ gap: 8, marginBottom: 24 }}>
        {TABS.map((item) => {
          const selected = item.key === tabKey;
          return (
            <Button
              key={item.key}
              fullWidth
              onClick={() => setTabKey(item.key)}
              style={{
                textTransform: 'none',
                borderRadius: 10,
                padding: '8px 12px',
                fontWeight: 'bold',
                border: `1px solid ${selected ? generalColor : '#E0E0E0'}`,
                background: selected ? generalColor : 'transparent',
                color: selected ? '#fff' : '#6B6B6B',
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  function renderPodium(top) {
    const order =
      top.length > 2
        ? [top[1], top[0], top[2]]
        : top.length === 2
        ? [top[1], top[0]]
        : [top[0]];

    return (
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="center"
        style={{ gap: 8, marginBottom: 24 }}
      >
        {order.map((item, index) => {
          if (!item) {
            return <Box key={`empty-${index}`} style={{ flex: 1 }} />;
          }
          const color = PODIUM_COLORS[item.position];
          return (
            <Box
              key={item.user_id}
              display="flex"
              flexDirection="column"
              alignItems="center"
              style={{ flex: 1, maxWidth: '33%' }}
            >
              {renderAvatar(item, item.position === 1 ? 64 : 52, color)}
              <Typography
                variant="body2"
                align="center"
                title={item.name}
                noWrap
                style={{
                  fontWeight: 'bold',
                  marginTop: 8,
                  maxWidth: '100%',
                }}
              >
                {isCurrentUser(item) ? 'Você' : shortName(item.name)}
              </Typography>
              <Typography
                variant="caption"
                align="center"
                style={{ color: '#9A9A9A', marginBottom: 8, minHeight: 20 }}
              >
                {formatValue(tabKey, item.value)}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  width: '100%',
                  height: PODIUM_HEIGHTS[item.position],
                  background: color,
                  borderRadius: '6px 6px 0 0',
                }}
              >
                <Typography
                  variant="h6"
                  style={{ color: '#fff', fontWeight: 'bold' }}
                >
                  {item.position}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  function renderRow(item, key) {
    const current = isCurrentUser(item);
    return (
      <Box
        key={key}
        display="flex"
        alignItems="center"
        style={{
          padding: '8px 12px',
          borderRadius: 8,
          marginBottom: 4,
          background: current ? `${generalColor}1F` : 'transparent',
        }}
      >
        <Typography
          variant="body2"
          style={{ width: 28, color: current ? generalColor : '#9A9A9A' }}
        >
          {item.position || '-'}
        </Typography>
        <Box style={{ marginRight: 12 }}>
          {renderAvatar(item, 32, current ? generalColor : '#C4C4C4')}
        </Box>
        <Typography
          variant="body2"
          style={{ flex: 1, fontWeight: current ? 'bold' : 'normal' }}
        >
          {current ? 'Você' : item.name}
        </Typography>
        <Typography variant="body2" style={{ color: '#9A9A9A' }}>
          {formatValue(tabKey, item.value)}
        </Typography>
      </Box>
    );
  }

  function renderContent() {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" style={{ padding: 24 }}>
          <LoadingProgress>Carregando ranking...</LoadingProgress>
        </Box>
      );
    }

    if (error) {
      return (
        <Box>
          <Typography variant="body2" style={{ color: '#8A8A8A' }}>
            Não foi possível carregar o ranking.
          </Typography>
          <Button
            variant="outlined"
            onClick={onRetry}
            style={{
              marginTop: 12,
              textTransform: 'none',
              color: generalColor,
              borderColor: generalColor,
            }}
          >
            Tentar de novo
          </Button>
        </Box>
      );
    }

    if (!data || data.top.length < 1) {
      return (
        <Typography variant="body2" style={{ color: '#8A8A8A' }}>
          {tab.empty}
        </Typography>
      );
    }

    const rest = data.top.slice(3);
    const userOutsideTop = data.user?.position && data.user.position > data.top.length;

    return (
      <>
        {renderPodium(data.top)}
        {rest.map((item) => renderRow(item, item.user_id))}
        {userOutsideTop ? (
          <>
            <Typography
              variant="caption"
              style={{ color: '#9A9A9A', paddingLeft: 12 }}
            >
              Sua posição
            </Typography>
            {renderRow(data.user, 'current-user')}
          </>
        ) : null}
        {!data.user?.position ? (
          <Typography
            variant="caption"
            style={{ color: '#9A9A9A', paddingLeft: 12 }}
          >
            Você ainda não entrou neste ranking.
          </Typography>
        ) : null}
      </>
    );
  }

  return (
    <AlertDialogSlide visible={visible} onClose={onClose} maxWidth="sm" hiddenTitle>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        style={{ paddingTop: 24, marginBottom: 24 }}
      >
        <Box>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Ranking {journeyName ? `· ${journeyName}` : ''}
          </Typography>
          <Typography variant="body2" style={{ color: '#8A8A8A' }}>
            Veja como você está em relação aos outros participantes
          </Typography>
        </Box>
        <IconButton size="small" aria-label="fechar" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      {renderTabs()}
      {renderContent()}
    </AlertDialogSlide>
  );
}

JourneyRanking.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  ranking: PropTypes.object,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  journeyName: PropTypes.string,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
};

export default JourneyRanking;
